import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, CardBody, Button, Spinner } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import { useTranslation } from 'react-i18next';

/**
 * Interstitial page for deep links on mobile devices
 * Shows app install prompt for mobile users, continues to web for desktop
 */
const AppInstallPrompt = () => {
    const { t } = useTranslation();
    const { uuid } = useParams();
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [platform, setPlatform] = useState(null);

    useEffect(() => {
        // Detect if user is on a mobile device
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;

        // Check for iOS
        if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
            setIsMobile(true);
            setPlatform('ios');
            setIsLoading(false);
            return;
        }

        // Check for Android
        if (/android/i.test(userAgent)) {
            setIsMobile(true);
            setPlatform('android');
            setIsLoading(false);
            return;
        }

        // Desktop - redirect directly to asset detail
        setIsMobile(false);
        setIsLoading(false);

        // Check if user is authenticated
        const authUser = localStorage.getItem('authUser');
        if (authUser) {
            navigate(`/asset-detail/${uuid}`, { replace: true });
        } else {
            // Store intended destination for post-login redirect
            localStorage.setItem('pendingDeepLink', uuid);
            navigate('/login', { replace: true });
        }
    }, [uuid, navigate]);

    const handleContinueToWeb = () => {
        const authUser = localStorage.getItem('authUser');
        if (authUser) {
            navigate(`/asset-detail/${uuid}`);
        } else {
            // Store intended destination for post-login redirect
            localStorage.setItem('pendingDeepLink', uuid);
            navigate('/login');
        }
    };

    const handleOpenAppStore = () => {
        window.location.href =
          'https://apps.apple.com/app/arcmedx/id6748859762';
    };

    const handleOpenPlayStore = () => {
        window.location.href = 'https://play.google.com/store/apps/details?id=com.umc.arcmedx';
    };

    if (isLoading) {
        return (
            <div className="page-content" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spinner color="primary" />
            </div>
        );
    }

    // Only render the interstitial on mobile
    if (!isMobile) {
        return null;
    }

    return (
        <div className="page-content" style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
            <Container>
                <Row className="justify-content-center">
                    <Col lg={6} md={8}>
                        <Card className="shadow-lg border-0" style={{ marginTop: '10vh' }}>
                            <CardBody className="text-center p-5">
                                {/* App Logo */}
                                <div className="mb-4">
                                    <div
                                        className="d-inline-flex align-items-center justify-content-center rounded-circle"
                                        style={{
                                            width: '80px',
                                            height: '80px',
                                            backgroundColor: '#6366f1',
                                            boxShadow: '0 4px 14px rgba(99, 102, 241, 0.3)'
                                        }}
                                    >
                                        <FeatherIcon icon="smartphone" size={40} style={{ color: 'white' }} />
                                    </div>
                                </div>

                                {/* Title */}
                                <h2 className="mb-3" style={{ color: '#1a1a2e', fontWeight: 600 }}>
                                    {t('app_install.get_app', 'Get the ArcMedix App')}
                                </h2>

                                {/* Description */}
                                <p className="text-muted mb-4" style={{ fontSize: '1rem' }}>
                                    {t('app_install.description', 'For the best experience managing your medical assets, download our mobile app.')}
                                </p>

                                {/* App Store Buttons */}
                                <div className="d-grid gap-3 mb-4">
                                    {platform === 'ios' && (
                                        <Button
                                            color="dark"
                                            size="lg"
                                            onClick={handleOpenAppStore}
                                            className="d-flex align-items-center justify-content-center"
                                            style={{ borderRadius: '12px', padding: '12px 24px' }}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="me-2">
                                                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                                            </svg>
                                            {t('app_install.download_ios', 'Download on App Store')}
                                        </Button>
                                    )}

                                    {platform === 'android' && (
                                        <Button
                                            color="success"
                                            size="lg"
                                            onClick={handleOpenPlayStore}
                                            className="d-flex align-items-center justify-content-center"
                                            style={{ borderRadius: '12px', padding: '12px 24px' }}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="me-2">
                                                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                                            </svg>
                                            {t('app_install.download_android', 'Get it on Google Play')}
                                        </Button>
                                    )}
                                </div>

                                {/* Divider */}
                                <div className="d-flex align-items-center my-4">
                                    <hr className="flex-grow-1" />
                                    <span className="px-3 text-muted" style={{ fontSize: '0.875rem' }}>{t('shared.or', 'or')}</span>
                                    <hr className="flex-grow-1" />
                                </div>

                                {/* Continue to Web Button */}
                                <Button
                                    color="link"
                                    onClick={handleContinueToWeb}
                                    className="text-decoration-none"
                                    style={{ color: '#6366f1', fontWeight: 500 }}
                                >
                                    {t('app_install.continue_web', 'Continue to Web Version')}
                                    <FeatherIcon icon="arrow-right" size={16} className="ms-2" />
                                </Button>

                                {/* UUID Display */}
                                <div className="mt-4 pt-4 border-top">
                                    <p className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                                        {t('shared.asset_tag_id', 'Asset Tag ID')}
                                    </p>
                                    <code style={{
                                        fontSize: '0.75rem',
                                        backgroundColor: '#f1f3f4',
                                        padding: '4px 8px',
                                        borderRadius: '4px'
                                    }}>
                                        {uuid}
                                    </code>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default AppInstallPrompt;
